import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface DestructiveConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isProcessing?: boolean;
}

export const DestructiveConfirmModal: React.FC<DestructiveConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  isProcessing = false
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!isOpen) setInputValue('');
  }, [isOpen]);

  if (!isOpen) return null;

  const isValid = inputValue.toLowerCase() === 'delete';

  const handleConfirm = () => {
    if (isValid && !isProcessing) onConfirm();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleConfirm();
  };

  const handleCancel = () => {
    if (!isProcessing) onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={24} color="#fa5252" />
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{title}</h2>
          </div>
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            style={{
              background: 'none',
              border: 'none',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              opacity: isProcessing ? 0.5 : 1
            }}
          >
            <X size={20} color="#868e96" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <p style={{ margin: '0 0 20px', color: '#495057', fontSize: '15px', lineHeight: '1.6' }}>
            {message}
          </p>
          <p style={{ margin: '0 0 8px', color: '#495057', fontSize: '14px' }}>
            Type <strong>delete</strong> to confirm:
          </p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={isProcessing}
            placeholder="delete"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${isValid ? '#fa5252' : '#ced4da'}`,
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s'
            }}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            background: '#f8f9fa'
          }}
        >
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#495057',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isProcessing ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || isProcessing}
            style={{
              padding: '8px 16px',
              background: !isValid || isProcessing ? '#adb5bd' : '#fa5252',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: !isValid || isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => {
              if (isValid && !isProcessing) e.currentTarget.style.background = '#e03131';
            }}
            onMouseOut={(e) => {
              if (isValid && !isProcessing) e.currentTarget.style.background = '#fa5252';
            }}
          >
            {isProcessing ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};
